import type { RefObject } from 'react';
import { useMemo } from 'react';

import { Box, Tab, Tabs } from '@mui/material';

import type { CronScheduleBuilderHandle } from '@/components/cron';
import type { CronTimeFrame, ScheduleType } from '@/types/schedule';

import type { AdvancedCronEditableScope } from '../types/advancedCronMigration';
import type { DailyFormState, RepeatingFormState } from '../types/scheduleFormState';
import type { CronScheduleSaveState } from '../utils/cronScheduleSave';
import { computeSchedulePreviewRuns } from '../utils/schedulePreviewRuns';
import { getBrowserTimezone } from '../utils/timezoneData';
import { CronScheduleConfig } from './CronScheduleConfig';
import { DailyScheduleConfig } from './DailyScheduleConfig';
import { RepeatingScheduleConfig } from './RepeatingScheduleConfig';
import { ScheduleNextRunsPreview } from './ScheduleNextRunsPreview';
import { ScheduleTypeSelector } from './ScheduleTypeSelector';

export interface CronMigrationProps {
  editableScope?: AdvancedCronEditableScope;
  timeframeUnconfirmed?: boolean;
  showStoredUtcExpression?: boolean;
  onConfirmTimezone?: () => void;
  onConfirmTimeframe?: () => void;
  onTimezoneMigrationChange?: () => void;
}

export interface ScheduleConfigPanelProps {
  scheduleType: ScheduleType;
  onScheduleTypeChange: (type: ScheduleType) => void;
  daily: DailyFormState;
  onDailyChange: (daily: DailyFormState) => void;
  repeating: RepeatingFormState;
  onRepeatingChange: (repeating: RepeatingFormState) => void;
  cron: CronScheduleSaveState;
  onCronChange: (cron: CronScheduleSaveState) => void;
  cronBuilderRef: RefObject<CronScheduleBuilderHandle | null>;
  typeSelectorVariant?: 'radio' | 'tabs';
  showLegacyMigrationNotice?: boolean;
  repeatingLegacyNoTimeZone?: boolean;
  cronMigration?: CronMigrationProps;
  storedCronTimeZone?: string;
  storedCronTimeFrame?: CronTimeFrame | null;
  disabled?: boolean;
}

export const ScheduleConfigPanel = ({
  scheduleType,
  onScheduleTypeChange,
  daily,
  onDailyChange,
  repeating,
  onRepeatingChange,
  cron,
  onCronChange,
  cronBuilderRef,
  typeSelectorVariant = 'radio',
  showLegacyMigrationNotice = false,
  repeatingLegacyNoTimeZone = false,
  cronMigration,
  storedCronTimeZone,
  storedCronTimeFrame,
  disabled = false,
}: ScheduleConfigPanelProps) => {
  const displayTimeZone = useMemo(() => {
    if (scheduleType === 'daily') {
      return daily.timeZone;
    }
    if (scheduleType === 'repeating') {
      return repeating.timeZone.trim() || getBrowserTimezone();
    }
    return cron.timeZone.trim() || getBrowserTimezone();
  }, [cron.timeZone, daily.timeZone, repeating.timeZone, scheduleType]);

  const previewRuns = useMemo(() => {
    if (scheduleType === 'cron' && cron.isValid === false) {
      return [];
    }

    const liveCronPreview = cron.previewExpression?.trim();
    const migrationPreview =
      cronMigration?.editableScope === 'timezone-pending-confirmation' ||
      (cronMigration?.editableScope === 'timeframe-pending-confirmation' && !cron.isDirty);

    let cronExpression = cron.expression;
    if (scheduleType === 'cron') {
      cronExpression = migrationPreview ? cron.expression : liveCronPreview || cron.expression;
    }

    return computeSchedulePreviewRuns({
      scheduleType,
      daily,
      repeating,
      cronExpression,
      displayTimeZone,
      repeatingLegacyNoTimeZone,
    });
  }, [
    scheduleType,
    daily,
    repeating,
    cron.expression,
    cron.previewExpression,
    cron.isValid,
    cron.isDirty,
    cronMigration?.editableScope,
    displayTimeZone,
    repeatingLegacyNoTimeZone,
  ]);

  return (
    <>
      {typeSelectorVariant === 'tabs' ? (
        <Tabs
          value={scheduleType}
          onChange={(_event, value: ScheduleType) => onScheduleTypeChange(value)}
        >
          <Tab label="Daily" value="daily" disabled={disabled} />
          <Tab label="Repeating" value="repeating" disabled={disabled} />
          <Tab label="Cron/Advanced" value="cron" disabled={disabled} />
        </Tabs>
      ) : (
        <Box sx={{ mb: 3 }}>
          <ScheduleTypeSelector
            value={scheduleType}
            onChange={onScheduleTypeChange}
            disabled={disabled}
          />
        </Box>
      )}

      <Box sx={typeSelectorVariant === 'radio' ? { pl: 2 } : undefined}>
        {scheduleType === 'daily' && (
          <DailyScheduleConfig value={daily} onChange={onDailyChange} disabled={disabled} />
        )}

        {scheduleType === 'repeating' && (
          <RepeatingScheduleConfig
            value={repeating}
            onChange={onRepeatingChange}
            disabled={disabled}
            preserveEmptyTimeZone={repeatingLegacyNoTimeZone}
          />
        )}

        {scheduleType === 'cron' && (
          <CronScheduleConfig
            builderRef={cronBuilderRef}
            value={cron}
            onChange={onCronChange}
            disabled={disabled}
            storedCronTimeZone={storedCronTimeZone}
            storedCronTimeFrame={storedCronTimeFrame}
            showLegacyMigrationNotice={showLegacyMigrationNotice}
            editableScope={cronMigration?.editableScope}
            timeframeUnconfirmed={cronMigration?.timeframeUnconfirmed}
            showStoredUtcExpression={cronMigration?.showStoredUtcExpression}
            onConfirmTimezone={cronMigration?.onConfirmTimezone}
            onConfirmTimeframe={cronMigration?.onConfirmTimeframe}
            onTimezoneMigrationChange={cronMigration?.onTimezoneMigrationChange}
          />
        )}
      </Box>

      <ScheduleNextRunsPreview runs={previewRuns} displayTimeZone={displayTimeZone} />
    </>
  );
};
